// Returns the normalized URL if it has a safe http(s) scheme, else null — blocks javascript:/data:/malformed URLs from reaching a clickable href. Returns parsed.toString() so the rendered href is exactly the value that passed validation.
export function safeHttpUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.toString() : null;
  } catch {
    return null;
  }
}

// https-only variant — matches StreamingText's rehype-sanitize src allow-list.
export function safeHttpsUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' ? parsed.toString() : null;
  } catch {
    return null;
  }
}

// Placeholder origin to re-resolve relative paths like a browser (`.invalid` is never a real host); mirrors safeReturnTo.
const PARSE_ORIGIN = 'https://lookout.invalid';

// Application-owned allow-list for agent-authored markdown link targets (StreamingText / AI-1065).
// Allowed: https, mailto (no query/body params), in-page fragments, and same-origin relative paths.
export function safeAgentMarkdownHref(url: string | null | undefined): string | null {
  if (!url) return null;
  // In-page fragment (footnote/anchor): same-document ref, no scheme, cannot execute — reject only whitespace/HTML-breaking chars.
  if (url.startsWith('#')) {
    return /^#[^\s<>"']+$/.test(url) ? url : null;
  }
  const https = safeHttpsUrl(url);
  if (https) return https;
  try {
    const parsed = new URL(url);
    // Drop mailto query params (?subject/?body) — pre-filled bodies are a phishing lever in agent output (AI-1065).
    return parsed.protocol === 'mailto:' && parsed.pathname ? `mailto:${parsed.pathname}` : null;
  } catch {
    // No absolute scheme → same-origin relative link. Validate the tab/newline/CR-stripped form the browser
    // actually resolves, else `/\t/host` slips past the protocol-relative guard and resolves cross-origin.
    const stripped = url.replace(/[\t\n\r]/g, '');
    if (!/^(\/|\.\.?\/|\?)/.test(stripped)) return null;
    // Protocol-relative (//host and the /\, \/, \\ variants browsers normalize to it) → cross-origin.
    if (/^[/\\]{2}/.test(stripped)) return null;
    // Percent-encoded slash/dot in the path survives normalization as traversal/route-swap — reject (query/fragment excluded).
    const path = stripped.replace(/[?#].*/, '');
    if (/%2e|%2f/i.test(path)) return null;
    // Literal `..` segment (terminated by `/` `\` `?` `#` or end) climbs out of the current workspace route.
    if (/([\\/]|^)\.\.([\\/?#]|$)/.test(stripped)) return null;
    // Re-resolve like the browser and re-check origin — `/.//evil` stays same-origin but normalizes to protocol-relative `//evil`.
    let resolved: URL;
    try {
      resolved = new URL(stripped, PARSE_ORIGIN);
    } catch {
      return null;
    }
    if (resolved.origin !== PARSE_ORIGIN) return null;
    if ((resolved.pathname + resolved.search + resolved.hash).startsWith('//')) return null;
    return stripped;
  }
}
